<template>
  <studio-tool-shell
    title="Anamnese-Einstellungen"
    description="Fragen, Seiten und Haftungsausschlüsse für alle Fragebogen-Varianten zentral konfigurieren."
    icon="mdi-clipboard-text-outline">
    <template v-slot:actions>
      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="#009640" depressed class="primary-action" v-bind="attrs" v-on="on">
            <v-icon left>mdi-play-circle-outline</v-icon>
            Fragebogen testen
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dark class="test-menu">
          <v-list-item @click="showAnamneseTest()"
            ><v-list-item-icon><v-icon>mdi-account-heart-outline</v-icon></v-list-item-icon
            ><v-list-item-title>Anamnesebogen</v-list-item-title></v-list-item
          >
          <v-list-item @click="showAnamneseTest('employeeQuestionnaire')"
            ><v-list-item-icon><v-icon>mdi-account-tie-outline</v-icon></v-list-item-icon
            ><v-list-item-title>Mitarbeiterfragebogen</v-list-item-title></v-list-item
          >
          <v-list-item @click="showAnamneseTest('livestyleAnamnese')"
            ><v-list-item-icon><v-icon>mdi-leaf-circle-outline</v-icon></v-list-item-icon
            ><v-list-item-title>Lebensstilfragebogen</v-list-item-title></v-list-item
          >
        </v-list>
      </v-menu>
    </template>

    <section class="settings-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Fragebogen-Baukasten</div>
          <h2>Konfiguration nach Bereich</h2>
          <p>Fragen bearbeiten, Seiten sortieren und Einstellungen pro Studio verwalten.</p>
        </div>
        <span class="count-pill">{{ studios.length }} Bereiche</span>
      </div>

      <div v-if="isLoading" class="surface-state">
        <v-progress-circular indeterminate color="#58d388" size="42"></v-progress-circular>
        <p>Konfigurationen werden geladen...</p>
      </div>

      <v-expansion-panels v-else dark multiple flat v-model="panel" class="settings-panels">
        <v-expansion-panel v-for="studio of studios" :key="studio._id" class="settings-panel">
          <v-expansion-panel-header class="settings-panel-header">
            <div class="studio-summary">
              <span class="studio-mark"><v-icon>mdi-file-document-edit-outline</v-icon></span>
              <div>
                <strong>{{ studio.name }}</strong
                ><span>{{ (studio.anamneseQuestions || []).length }} Fragen · {{ (studio.pages || []).length }} Seiten</span>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-expansion-panel
              v-if="studio._id !== 'nutrition' && studio._id !== 'livestyleAnamnese' && studio._id !== 'employeeQuestionnaire'"
              class="inner-panel">
              <v-expansion-panel-header class="font-weight-bold">
                <span class="inner-panel-title"><v-icon>mdi-tune-variant</v-icon>Allgemeine Einstellungen</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-select
                  @change="saveStudio(studio._id)"
                  label="Anamnesebogen aus anderem Studio übernehmen"
                  clearable
                  v-model="studio.anamneseSettings.copiedStudio"
                  :items="studios.map((studio) => ({ text: studio.name, value: studio._id }))"
                  outlined
                  dense
                  hide-details="auto"></v-select>

                <v-textarea
                  @blur="saveStudio(studio._id)"
                  label="Haftungsausschluss"
                  v-model="studio.anamneseSettings.disclaimer"
                  outlined
                  rows="5"
                  hide-details="auto"></v-textarea>
                <v-card-actions class="inner-actions">
                  <v-btn outlined color="#58d388" class="secondary-action" @click="generatePdf(studio)">
                    <v-icon left>mdi-file-pdf-box</v-icon>
                    Anamnesebogen als PDF
                  </v-btn>
                </v-card-actions>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :disabled="studio.anamneseSettings.copiedStudio" class="inner-panel">
              <v-expansion-panel-header class="font-weight-bold">
                <span class="inner-panel-title"><v-icon>mdi-help-circle-outline</v-icon>Fragen</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-row class="d-flex justify-center">
                  <v-col cols="12">
                    <v-list color="transparent" class="builder-list">
                      <v-list-item-group>
                        <v-list-item
                          @click="createUpdateItem(studio._id, questionDialog, question)"
                          class="builder-list-item"
                          v-for="(question, index) of studio.anamneseQuestions"
                          :key="index + '-question'">
                          <v-list-item-avatar class="item-index">{{ index + 1 }}</v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-bold">
                              {{ question.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle>{{ question.type || "Frage" }}{{ question.required ? " · Pflichtfeld" : "" }}</v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-col>

                  <v-col cols="12" class="d-flex justify-center">
                    <v-btn outlined color="#58d388" class="secondary-action" @click="createUpdateItem(studio._id, questionDialog)">
                      <v-icon left>mdi-plus</v-icon><span>Frage hinzufügen</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="studio.anamneseQuestions && studio.anamneseQuestions.length" class="inner-panel">
              <v-expansion-panel-header class="font-weight-bold">
                <span class="inner-panel-title"><v-icon>mdi-view-sequential-outline</v-icon>Seiten</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-row class="d-flex justify-center">
                  <v-col cols="12">
                    <v-list color="transparent" class="builder-list">
                      <draggable v-model="studio.pages" handle=".drag-handle" @change="updatePageSorting(studio._id)">
                        <v-list-item
                          class="builder-list-item"
                          @click="createUpdateItem(studio._id, pageDialog, page)"
                          v-for="(page, index) of studio.pages"
                          :key="index + '-question'">
                          <v-list-item-avatar class="drag-handle"><v-icon>mdi-drag</v-icon></v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-bold">
                              Seite {{ index + 1 }} · {{ page.displayedTitle || "Ohne Titel" }}
                            </v-list-item-title>
                            <v-list-item-subtitle>{{ (page.questions || []).length }} Fragen</v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                        </v-list-item>
                      </draggable>
                    </v-list>
                  </v-col>

                  <v-col cols="12" class="d-flex justify-center">
                    <v-btn outlined color="#58d388" class="secondary-action" @click="createUpdateItem(studio._id, pageDialog)">
                      <v-icon left>mdi-plus</v-icon><span>Seite hinzufügen</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <v-dialog
      :value="!!activeDialog"
      max-width="680px"
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @input="!$event && cancelCalled()">
      <v-card dark class="editor-card">
        <div class="editor-header">
          <span class="editor-mark"><v-icon>mdi-file-document-edit-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Fragebogen-Editor</div>
            <h2>{{ activeDialog }}</h2>
            <p>Inhalt und Verhalten konfigurieren.</p>
          </div>
          <v-btn icon class="editor-close" aria-label="Dialog schliessen" @click="cancelCalled"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="editor-body">
          <anamneseDashboardSettingsQuestionDialog
            ref="questionDialog"
            :studio="activeStudioID"
            :question="activeItem"
            @deleted="finishedQuestionDialog"
            @saveFinished="finishedQuestionDialog"
            v-if="activeDialog === questionDialog" />
          <anamneseDashboardSettingsPageDialog
            ref="pageDialog"
            :studio="activeStudioID"
            :page="activeItem"
            @deleted="finishedPageDialog"
            @saveFinished="finishedPageDialog"
            :questions="getNotYetSelectedQuestions(activeStudioID)"
            v-if="activeDialog === pageDialog" />
        </v-card-text>
        <v-card-actions class="editor-actions">
          <v-spacer></v-spacer>
          <v-btn text class="cancel-action" @click="cancelCalled">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" @click="saveCalled"><v-icon left>mdi-content-save-outline</v-icon>Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <anamnese-bogen-create ref="anamneseBogenRef"></anamnese-bogen-create>
  </studio-tool-shell>
</template>

<script>
import draggable from "vuedraggable"
import { jsPDF } from "jspdf"

import api from "@/api"
import anamneseDashboardSettingsQuestionDialog from "./anamneseDashboardSettingsQuestionDialog"
import anamneseDashboardSettingsPageDialog from "./anamneseDashboardSettingsPageDialog"
import anamneseBogenCreate from "../../../components/anamneseBogenCreate"
import StudioToolShell from "../studio/components/StudioToolShell.vue"

export default {
  components: {
    draggable,
    StudioToolShell,
    anamneseBogenCreate,
    anamneseDashboardSettingsQuestionDialog,
    anamneseDashboardSettingsPageDialog,
  },
  data() {
    return {
      panel: [],
      studios: [],
      isLoading: true,
      activeDialog: "",
      activeItem: null,
      activeStudioID: null,
      questionDialog: "Neue Frage erstellen",
      pageDialog: "Neue Seite erstellen",
    }
  },
  async created() {
    try {
      this.studios = await api.fetchAnamneseDashboardInfos()
    } catch (error) {
      console.error("Fehler beim Laden der Anamnese-Einstellungen:", error)
      this.$toast.error("Anamnese-Einstellungen konnten nicht geladen werden")
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    // Oben im <script> Bereich von anamneseDashboardSettings.vue importieren

    // In der methods: { ... } Sektion
    async generatePdf(studio) {
      // 1. Initialisierung
      const doc = new jsPDF("p", "mm", "a4")
      const pageW = doc.internal.pageSize.getWidth()
      const margin = 15
      const contentW = pageW - 2 * margin
      const lineHeight = 7
      let y = 20

      // ###############################################
      // ## NEU: 1. Die Wasserzeichen-Funktion definieren
      // ###############################################
      const addWatermark = (docInstance) => {
        const pageW = docInstance.internal.pageSize.getWidth()
        const pageH = docInstance.internal.pageSize.getHeight()

        // --- Wasserzeichen-Stil anwenden ---
        docInstance.setFont("helvetica", "bold") // Setzt Stil explizit
        docInstance.setFontSize(100)
        docInstance.setTextColor(150) // Helles Grau
        docInstance.setGState(new docInstance.GState({ opacity: 0.15 })) // Transparenz

        // --- Wasserzeichen-Text zeichnen ---
        docInstance.text("MUSTER", pageW / 2, pageH / 2, { angle: -45, align: "center" })

        // --- Grafischen Zustand für den restlichen Text vollständig zurücksetzen ---
        docInstance.setGState(new docInstance.GState({ opacity: 1.0 }))
        docInstance.setFont("helvetica", "normal") // WICHTIG: Stil zurücksetzen
        docInstance.setFontSize(12) // WICHTIG: Grösse auf einen Standardwert zurücksetzen
        docInstance.setTextColor(0, 0, 0) // Schriftfarbe auf Schwarz zurücksetzen
      }

      // Helper-Funktion für Seitenumbrüche
      const checkPageBreak = (neededHeight) => {
        if (y + neededHeight >= doc.internal.pageSize.getHeight() - margin) {
          doc.addPage()
          // ########################################################
          // ## NEU: 3. Wasserzeichen auf jeder neuen Seite anwenden
          // ########################################################
          addWatermark(doc)
          y = margin
        }
      }

      // ###############################################
      // ## NEU: 2. Wasserzeichen auf erster Seite anwenden
      // ###############################################
      addWatermark(doc)

      // 2. Dokument-Titel rendern
      doc.setFont("helvetica", "bold")
      doc.setFontSize(20)
      doc.text(`Anamnesebogen`, margin, y)
      y += lineHeight * 2
      doc.setFont("helvetica", "normal")

      // 3. Seiten und Fragen durchlaufen und zeichnen
      for (const page of studio.pages.sort((a, b) => (a.sort || 0) - (b.sort || 0))) {
        checkPageBreak(12)
        doc.setFontSize(16)
        doc.text(page.displayedTitle || `Seite`, margin, y)
        y += lineHeight * 1.5

        for (const question of page.questions) {
          // Frage-Titel (mit automatischem Zeilenumbruch)
          doc.setFontSize(12)
          doc.setFont("helvetica", "bold")
          const titleLines = doc.splitTextToSize(question.title + (question.required ? " *" : ""), contentW)
          checkPageBreak(titleLines.length * lineHeight + 5) // Platz für Titel + Antwort
          doc.text(titleLines, margin, y)
          y += titleLines.length * lineHeight
          doc.setFont("helvetica", "normal")

          // Antwort-Feld je nach Typ zeichnen
          if (question.type === "select") {
            y += 2 // Kleiner Abstand
            for (const option of question.options) {
              checkPageBreak(lineHeight * 1.5)
              doc.rect(margin, y - 4, 4, 4) // Checkbox
              doc.text(option, margin + 6, y)
              y += lineHeight

              // Zugehörige Zusatzfelder rendern
              const additionalFields = question.additionalFields.filter((f) => f.selection === option)
              for (const addField of additionalFields) {
                checkPageBreak(lineHeight * 2)
                doc.setFontSize(10)
                doc.setFont("helvetica", "italic")
                const addFieldTitle = addField.title + (addField.required ? " *" : "")
                doc.text(addFieldTitle, margin + 6, y)
                y += lineHeight
                doc.setDrawColor(180, 180, 180)
                doc.line(margin + 6, y, margin + contentW - 20, y) // Kürzere Linie für Antwort
                doc.setDrawColor(0, 0, 0)
                y += lineHeight
                doc.setFont("helvetica", "normal")
                doc.setFontSize(12)
              }
            }
            y += 5 // Abstand nach der Auswahl-Frage
          } else if (question.type === "rating") {
            y += 3
            checkPageBreak(lineHeight)
            for (let i = 0; i < (question.maxStarCount || 5); i++) {
              doc.circle(margin + i * 8 + 3, y, 3) // Sterne als Kreise
            }
            y += lineHeight + 3
          } else {
            // text, number, etc.
            y += 2
            checkPageBreak(lineHeight)
            doc.setDrawColor(180, 180, 180)
            doc.line(margin, y, margin + contentW, y) // Linie für die Antwort
            doc.setDrawColor(0, 0, 0)
            y += lineHeight + 3
          }
        }
      }

      // 4. Haftungsausschluss auf einer neuen Seite
      if (studio.anamneseSettings && studio.anamneseSettings.disclaimer) {
        doc.addPage()
        // ########################################################
        // ## NEU: Wasserzeichen auch auf dieser Seite anwenden
        // ########################################################
        addWatermark(doc)
        y = margin
        doc.setFontSize(16)
        doc.text("Haftungsausschluss", margin, y)
        y += lineHeight * 1.5

        doc.setFontSize(10)
        const disclaimerLines = doc.splitTextToSize(studio.anamneseSettings.disclaimer, contentW)
        doc.text(disclaimerLines, margin, y)
      }

      // 5. Unterschriftenfelder
      const signatureY = doc.internal.pageSize.getHeight() - 40
      // Prüfen, ob für die Unterschriften eine neue Seite nötig ist
      if (y > signatureY) {
        doc.addPage()
        addWatermark(doc)
      }
      doc.line(margin, signatureY, margin + 70, signatureY)
      doc.text("Datum, Ort", margin, signatureY + 5)

      doc.line(pageW - margin - 70, signatureY, pageW - margin, signatureY)
      doc.text("Unterschrift Kunde/Kundin", pageW - margin - 70, signatureY + 5)

      // 6. PDF speichern
      doc.save(`MUSTER_Anamnesebogen_${studio.name.replace(/\s+/g, "_")}.pdf`)
    },

    getNotYetSelectedQuestions(studioID) {
      let pages = this.studios.find((studio) => studio._id === studioID).pages
      let questions = []
      pages.forEach((page) => {
        page.questions.forEach((question) => {
          questions.push(question._id)
        })
      })
      return this.studios.find((studio) => studio._id === studioID).anamneseQuestions.filter((question) => !questions.includes(question._id))
    },
    showAnamneseTest(variant = null) {
      if (!variant) return this.$refs.anamneseBogenRef.setVisible()

      this.$refs.anamneseBogenRef.setVisible(null, null, variant, null, null)
    },
    async updatePageSorting(studioId) {
      let pages = this.studios.find((studio) => studio._id === studioId).pages
      for (let i = 0; i < pages.length; i++) {
        pages[i].sort = i
      }
      await api.updateAnamnesePageSorting(pages)
      this.$toast.success("Seiten wurden erfoglreich sortiert")
    },
    createUpdateItem(studioID, dialogType, item = null) {
      this.activeDialog = dialogType
      this.activeStudioID = studioID
      this.activeItem = item
    },
    async saveStudio(id) {
      await api.updateStudio(this.studios.find((studio) => studio._id === id))
      this.$toast.success("Studio wurde erfolgreich gespeichert")
    },
    saveCalled() {
      this.activeDialog === this.questionDialog && this.$refs.questionDialog.createQuestion()
      this.activeDialog === this.pageDialog && this.$refs.pageDialog.createPage()
    },
    cancelCalled() {
      this.activeDialog = ""
      this.activeItem = null
    },
    finishedQuestionDialog(newQuestion = null) {
      if (newQuestion) {
        this.studios = this.studios.map((studio) => {
          if (studio._id === newQuestion.studio) {
            if (studio.anamneseQuestions.find((question) => question._id === newQuestion._id))
              studio.anamneseQuestions = studio.anamneseQuestions.map((question) => {
                if (question._id === newQuestion._id) return newQuestion
                return question
              })
            else studio.anamneseQuestions.push(newQuestion)
          }
          return studio
        })
        this.$toast.success("Frage erfolgreich gespeichert")
      } else {
        this.studios = this.studios.map((studio) => {
          if (studio._id === this.activeStudioID) {
            studio.anamneseQuestions = studio.anamneseQuestions.filter((question) => question._id !== this.activeItem._id)
          }
          return studio
        })
        this.$toast.error("Frage erfolgreich gelöscht")
      }
      this.activeDialog = ""
      this.activeItem = null
    },
    finishedPageDialog(newPage = null) {
      if (newPage) {
        this.studios = this.studios.map((studio) => {
          if (studio._id === newPage.studio) {
            if (studio.pages.find((page) => page._id === newPage._id))
              studio.pages = studio.pages.map((page) => {
                if (page._id === newPage._id) return newPage
                return page
              })
            else studio.pages.push(newPage)
          }
          return studio
        })
        this.$toast.success("Seite erfolgreich gespeichert")
      } else {
        this.studios = this.studios.map((studio) => {
          if (studio._id === this.activeStudioID) {
            studio.pages = studio.pages.filter((page) => page._id !== this.activeItem._id)
          }
          return studio
        })
        this.$toast.error("Seite erfolgreich gelöscht")
      }
      this.activeDialog = ""
      this.activeItem = null
    },
  },
}
</script>

<style scoped>
.settings-surface {
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background: var(--surface);
}

.surface-header,
.studio-summary,
.inner-panel-title,
.editor-header {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.editor-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text, #f4f4f5);
  font-size: 1.25rem;
}

.surface-header p,
.surface-state p,
.editor-header p {
  margin: 0.25rem 0 0;
  color: var(--muted, #a3a3a8);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill {
  display: inline-flex;
  flex: 0 0 auto;
  padding: 0.4375rem 0.75rem;
  border-radius: 999px;
  color: #d9f8e5;
  background: rgba(0, 150, 64, 0.14);
  font-size: 0.75rem;
  font-weight: 750;
}

.surface-state {
  display: grid;
  min-height: 16rem;
  place-items: center;
  align-content: center;
  text-align: center;
}

.settings-panels {
  gap: 0.75rem;
}

.settings-panel {
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1.125rem !important;
  background: rgba(20, 20, 21, 0.76) !important;
}

.settings-panel::before,
.inner-panel::before {
  box-shadow: none !important;
}

.settings-panel-header {
  min-height: 5rem;
  padding: 0.875rem 1rem;
}

.studio-summary {
  min-width: 0;
  gap: 0.75rem;
}

.studio-mark,
.editor-mark {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.studio-mark {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
}

.studio-summary > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.studio-summary strong {
  overflow: hidden;
  color: var(--text);
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.studio-summary span:last-child {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.settings-panel ::v-deep > .v-expansion-panel-content > .v-expansion-panel-content__wrap {
  padding: 0 1rem 1rem;
}

.inner-panel {
  margin-top: 0.75rem;
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1rem !important;
  background: rgba(255, 255, 255, 0.025) !important;
}

.inner-panel-title {
  gap: 0.625rem;
  color: var(--text);
  font-size: 1rem;
}

.inner-panel-title .v-icon {
  color: #58d388;
}

.inner-panel ::v-deep .v-expansion-panel-content__wrap {
  padding: 0.75rem 1rem 1rem;
}

.inner-actions {
  justify-content: flex-end;
  padding: 0.75rem 0 0 !important;
}

.builder-list {
  display: grid;
  gap: 0.5rem;
}

.builder-list-item {
  min-height: 4rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.028);
}

.builder-list-item + .builder-list-item {
  margin-top: 0.5rem;
}

.item-index,
.drag-handle {
  display: grid;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
  font-size: 0.75rem;
  font-weight: 800;
}

.drag-handle {
  cursor: grab;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.secondary-action {
  color: #a6edc0 !important;
}

.test-menu {
  min-width: 17rem;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 0.875rem;
  background: #202022 !important;
}

.editor-card {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  color: var(--text);
  background: #1c1c1d !important;
  font-size: 1rem;
}

.editor-header {
  flex: 0 0 auto;
  gap: 0.875rem;
  padding: 1.25rem 1.375rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
}

.editor-mark {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
}

.editor-header > div {
  min-width: 0;
}

.editor-close {
  margin-left: auto;
  color: #ffffff !important;
}

.editor-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-actions {
  flex: 0 0 auto;
  min-height: 4.75rem;
  gap: 0.5rem;
  padding: 0.875rem 1.375rem !important;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.14);
}

.cancel-action {
  min-height: 2.75rem;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  text-transform: none;
}

.editor-body ::v-deep .px-3 {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.editor-body ::v-deep .v-input__slot,
.editor-body ::v-deep .v-card {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

@media (hover: hover) and (pointer: fine) {
  .builder-list-item:hover,
  .settings-panel:hover {
    border-color: var(--border-strong) !important;
  }
}

@media (max-width: 37.4375rem) {
  .settings-surface {
    padding: 0.875rem;
    border-radius: 1.125rem;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-panel-header {
    padding: 0.75rem;
  }

  .settings-panel ::v-deep > .v-expansion-panel-content > .v-expansion-panel-content__wrap {
    padding: 0 0.75rem 0.75rem;
  }

  .inner-panel ::v-deep .v-expansion-panel-content__wrap {
    padding: 0.625rem 0.75rem 0.75rem;
  }

  .editor-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .editor-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .editor-mark,
  .editor-header p {
    display: none;
  }

  .editor-body {
    padding: 0.75rem !important;
  }

  .editor-actions {
    flex-wrap: wrap;
    padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom)) !important;
  }

  .editor-actions .primary-action {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
